import { AsyncPipe, NgIf } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { EmptyStateModule } from 'app/b2b-order/components/empty-state/empty-state.module';
import { FcpButtonModule } from 'fc-ui-components';
import { BrandAccessRight, pathToProductNameMap } from 'helpers';
import * as _ from 'lodash';
import { AccessState, FileModel } from 'models';
import { Subject, Subscription } from 'rxjs';
import { ConfirmDialogModule } from 'components/modal';
import { FeaturedBrand } from 'models/brand/types';
import { ModalService, UserService } from 'services';
import {
    getProductsBrandParticipates,
    getProductsWithTerms,
    prepareBrandRequestedProducts
} from 'app/retailer-platform/brands/helpers';

import { RequestAccessModalModule } from 'components/modal/request-access-modal/request-access-modal.module';
import { BrandAccessService, BrandData } from 'app/retailer-platform/brands/providers/brand-access.service';
import { DirectivesModule } from 'app/directives/directives.module';
import { StoreService } from 'app/b2b-order/services/store.service';
import { CreateStoreDialogModule } from 'app/b2b-order/components/create-store/create-store-dialog.module';

@Component({
    selector: 'fc-no-access-product',
    templateUrl: './no-access-product.component.html',
    imports: [
        EmptyStateModule,
        TranslateModule,
        FcpButtonModule,
        AsyncPipe,
        NgIf,
        DirectivesModule,
        RequestAccessModalModule,
        ConfirmDialogModule,
        CreateStoreDialogModule
    ]
})
export class NoAccessProductComponent implements OnInit {
    public readonly requestAccessWithNoteModalId = 'discovery.request-access-with-note';
    public readonly requestCreateStoreModalId = 'request-access-create-store';
    public readonly createStoreModalId = 'no-access-create-store';
    public productName: string;

    constructor(
        private activatedRoute: ActivatedRoute,
        private brandAccessService: BrandAccessService,
        private userService: UserService,
        private modalService: ModalService,
        private storeService: StoreService
    ) {
        this.productName = pathToProductNameMap[this.extractUriAfterBrandId(window.location.pathname)];
    }

    public requestedProductTermsForModal: {
        pdfLink: string;
        pdfLinkTranslationKey: string;
    }[] = [];
    private requestedProductsFromBrand: BrandAccessRight[];
    public noteForConnection = '';
    public brand: BrandData;
    public accessState: AccessState = AccessState.NotRequested;

    public productAccessRight: Subject<AccessState> = new Subject<AccessState>();
    public queryParamSubscription: Subscription;
    public storesSubscription: Subscription;
    public retailerTermsOfUse: FileModel;
    private hasStores = false;

    ngOnInit() {
        this.productAccessRight.subscribe((accessState: AccessState) => {
            this.accessState = accessState;
        });
        this.queryParamSubscription = this.activatedRoute.queryParamMap.subscribe((queryParams) => {
            const brandId = extractBrandIdFromUrl(window.location.pathname);
            this.brandAccessService.getBrands().subscribe((brands) => {
                this.brand = brands.find((brand) => brand._id === brandId);
                const reorderAcessRight = this.brand.relations[this.productName];
                this.productAccessRight.next(reorderAcessRight);
            });
        });

        this.storesSubscription = this.storeService.stores.subscribe((stores) => {
            this.hasStores = !_.isEmpty(stores);
        });
    }

    public openAccessRequestWithNoteModal() {
        this.requestedProductsFromBrand = getProductsBrandParticipates(this.brand);

        this.requestedProductTermsForModal = prepareBrandRequestedProducts(
            this.brand.termsOfUse,
            getProductsWithTerms(this.brand.termsOfUse, this.requestedProductsFromBrand),
            this.userService.getPreferredLanguage(),
            this.userService.getOrganization().address.country
        );

        this.modalService.open(this.requestAccessWithNoteModalId);
    }

    public connectWithNote(data: { message; retailerTermsFile }) {
        this.handleConnection(this.brand, this.requestedProductsFromBrand, data.message, data.retailerTermsFile);
    }

    public clearConnectionData() {
        this.noteForConnection = '';
        this.requestedProductTermsForModal = [];
    }

    public openCreateStoreModal() {
        this.modalService.close(this.requestCreateStoreModalId);
        this.modalService.open(this.createStoreModalId);
    }

    public sendRequestWithoutStore() {
        this.handleConnection(
            this.brand,
            [BrandAccessRight.MarketingMaterial, BrandAccessRight.ProductData],
            this.noteForConnection,
            this.retailerTermsOfUse
        );
    }

    public sendRequestWithStore() {
        this.handleConnection(
            this.brand,
            this.requestedProductsFromBrand,
            this.noteForConnection,
            this.retailerTermsOfUse
        );
    }

    public getTranslationKey(item) {
        return this.productsToTranslationsKeysMap[this.productName]?.[this.accessState]?.[item];
    }

    public openNoStoreModal() {
        this.modalService.open(this.requestCreateStoreModalId);
    }

    private handleConnection(
        brand: BrandData | FeaturedBrand,
        productsToRequest: BrandAccessRight[],
        note: string,
        termsOfUseFile: FileModel
    ) {
        if (
            !this.hasStores &&
            (_.includes(productsToRequest, BrandAccessRight.ReorderPlatform) ||
                _.includes(productsToRequest, BrandAccessRight.EndlessAisle) ||
                _.includes(productsToRequest, BrandAccessRight.PreorderPlatform))
        ) {
            this.noteForConnection = note;
            this.retailerTermsOfUse = termsOfUseFile;
            return this.modalService.open(this.requestCreateStoreModalId);
        }

        this.clearConnectionData();
        return this.brandAccessService.sendAccessRequestForBrand(brand, productsToRequest, note, termsOfUseFile as any);
    }

    private extractUriAfterBrandId(url: string): string | null {
        const regex = /\/brand\/([^/]+)(\/.*)/;
        const match = url.match(regex);

        if (match && match[2]) {
            return match[2];
        }

        console.error('Invalid URL format');
        return null;
    }

    protected readonly AccessState = AccessState;

    // reuse-no-access: for each new product, add the translation keys for NotRequested and Pending states
    protected readonly productsToTranslationsKeysMap = {
        [BrandAccessRight.PreorderPlatform]: {
            [AccessState.NotRequested]: {
                header: 'ORDER.EMPTY_STATE.NO_ACCESS.HEADER',
                description1: 'PREORDER.EMPTY_STATE.NO_ACCESS.DESCRIPTION1',
                description2: 'ORDER.EMPTY_STATE.NO_ACCESS.DESCRIPTION2'
            },
            [AccessState.Pending]: {
                header: 'ORDER.EMPTY_STATE.ACCESS_PENDING.HEADER',
                description1: 'ORDER.EMPTY_STATE.ACCESS_PENDING.DESCRIPTION1',
                description2: 'ORDER.EMPTY_STATE.NO_ACCESS.DESCRIPTION2'
            }
        },
        [BrandAccessRight.ReorderPlatform]: {
            [AccessState.NotRequested]: {
                header: 'ORDER.EMPTY_STATE.NO_ACCESS.HEADER',
                description1: 'REORDER.EMPTY_STATE.NO_ACCESS.DESCRIPTION1',
                description2: 'ORDER.EMPTY_STATE.NO_ACCESS.DESCRIPTION2'
            },
            [AccessState.Pending]: {
                header: 'ORDER.EMPTY_STATE.ACCESS_PENDING.HEADER',
                description1: 'ORDER.EMPTY_STATE.ACCESS_PENDING.DESCRIPTION1',
                description2: 'ORDER.EMPTY_STATE.NO_ACCESS.DESCRIPTION2'
            }
        },
        [BrandAccessRight.MarketingMaterial]: {
            [AccessState.NotRequested]: {
                header: 'CONTENT.EMPTY_STATE.NO_ACCESS.HEADER',
                description1: 'CONTENT_MARKETING_MATERIAL.EMPTY_STATE.NO_ACCESS.DESCRIPTION1',
                description2: 'CONTENT.EMPTY_STATE.NO_ACCESS.DESCRIPTION2'
            },
            [AccessState.Pending]: {
                header: 'CONTENT.EMPTY_STATE.ACCESS_PENDING.HEADER',
                description1: 'CONTENT.EMPTY_STATE.ACCESS_PENDING.DESCRIPTION1',
                description2: 'CONTENT.EMPTY_STATE.NO_ACCESS.DESCRIPTION2'
            }
        },
        [BrandAccessRight.ProductData]: {
            [AccessState.NotRequested]: {
                header: 'CONTENT.EMPTY_STATE.NO_ACCESS.HEADER',
                description1: 'CONTENT_PRODUCT_DATA.EMPTY_STATE.NO_ACCESS.DESCRIPTION1',
                description2: 'CONTENT.EMPTY_STATE.NO_ACCESS.DESCRIPTION2'
            },
            [AccessState.Pending]: {
                header: 'CONTENT.EMPTY_STATE.ACCESS_PENDING.HEADER',
                description1: 'CONTENT.EMPTY_STATE.ACCESS_PENDING.DESCRIPTION1',
                description2: 'CONTENT.EMPTY_STATE.NO_ACCESS.DESCRIPTION2'
            }
        }
    };
}

export const extractBrandIdFromUrl = (url: string) => {
    const regex = /\/r\/brand\/([a-f\d]{24}).*/;
    const matches = regex.exec(url);

    if (matches !== null) {
        return matches[1];
    }

    return null;
};
